import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const TABLES = [
  { key: 'support_plans',   label: 'Support Plans',   icon: 'fa-file-contract' },
  { key: 'product_types',   label: 'Product Types',   icon: 'fa-box'           },
  { key: 'platforms',       label: 'Platforms',       icon: 'fa-desktop'       },
  { key: 'sales_managers',  label: 'Sales Managers',  icon: 'fa-user-tie'      },
  { key: 'clients',         label: 'Clients',         icon: 'fa-building'      },
  { key: 'classifications', label: 'Classifications', icon: 'fa-tags'          },
  { key: 'categories',      label: 'Categories',      icon: 'fa-layer-group'   },
];

// ── Transfer Modal ────────────────────────────────────────────
function TransferModal({ item, items, onConfirm, onCancel }) {
  const [transferToId, setTransferToId] = useState('');
  const others = items.filter(i => String(i.id) !== String(item.id) && i.is_active);

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onCancel()}>
      <div className="modal" style={{maxWidth:500}}>
        <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:6}}>
          <div style={{width:36,height:36,borderRadius:'50%',background:'#FAEEDA',display:'flex',alignItems:'center',justifyContent:'center'}}>
            <i className="fa-solid fa-right-left" style={{color:'#854F0B',fontSize:16}}></i>
          </div>
          <div className="modal-title" style={{margin:0}}>Transfer & Delete</div>
        </div>

        <div style={{background:'#FAEEDA',border:'1px solid #EF9F27',borderRadius:8,padding:'10px 14px',marginBottom:16,fontSize:13,color:'#854F0B'}}>
          <strong>"{item.name}"</strong> is used by <strong>{item.ticket_count} ticket(s)</strong>.
          Choose where to transfer these tickets before deleting.
        </div>

        <div className="field" style={{marginBottom:20}}>
          <label>Transfer tickets to <span className="req">*</span></label>
          {others.length === 0 ? (
            <div style={{fontSize:13,color:'#e24b4a',padding:'8px 0'}}>
              <i className="fa-solid fa-circle-exclamation" style={{marginRight:6}}></i>
              No other active items available. Please add another item first.
            </div>
          ) : (
            <select value={transferToId} onChange={e => setTransferToId(e.target.value)}
              style={{width:'100%',padding:'8px 10px',border:'1px solid #dde1ec',borderRadius:7,fontSize:13}}>
              <option value="">Select replacement...</option>
              {others.map(o => (
                <option key={o.id} value={o.id}>
                  {o.name} ({o.ticket_count} ticket{o.ticket_count !== 1 ? 's' : ''})
                </option>
              ))}
            </select>
          )}
        </div>

        {transferToId && (
          <div style={{background:'#EAF3DE',border:'1px solid #97C459',borderRadius:8,padding:'10px 14px',marginBottom:16,fontSize:13,color:'#3B6D11'}}>
            <i className="fa-solid fa-circle-check" style={{marginRight:6}}></i>
            <strong>{item.ticket_count} ticket(s)</strong> will be updated from
            <strong> "{item.name}"</strong> →
            <strong> "{others.find(o => String(o.id) === String(transferToId))?.name}"</strong>,
            then <strong>"{item.name}"</strong> will be deleted.
          </div>
        )}

        <div style={{display:'flex',justifyContent:'flex-end',gap:10}}>
          <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
          <button
            className="btn btn-primary"
            disabled={!transferToId || others.length === 0}
            onClick={() => onConfirm(transferToId)}
            style={{background:'#e24b4a',borderColor:'#e24b4a'}}>
            <i className="fa-solid fa-right-left"></i> Transfer &amp; Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────
export default function DropdownManager() {
  const { isAdmin } = useAuth();
  const navigate    = useNavigate();

  const [activeTab, setActiveTab] = useState('support_plans');
  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(false);
  const [newName, setNewName]     = useState('');
  const [adding, setAdding]       = useState(false);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');
  const [search, setSearch]       = useState('');
  const [showAddModal, setShowAddModal] = useState(false);

  // Transfer modal state
  const [transferItem, setTransferItem] = useState(null); // item being deleted

  useEffect(() => {
    if (!isAdmin) { navigate('/'); return; }
  }, []);

  useEffect(() => {
    loadItems(activeTab);
    setNewName(''); setError(''); setSuccess(''); setSearch(''); setShowAddModal(false);
  }, [activeTab]);

  const loadItems = async (table) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/dropdowns/${table}`);
      setItems(data);
    } catch { setError('Failed to load items'); }
    finally  { setLoading(false); }
  };

  const addItem = async e => {
    e.preventDefault();
    if (!newName.trim()) return;
    setAdding(true); setError(''); setSuccess('');
    try {
      await api.post(`/admin/dropdowns/${activeTab}`, { name: newName.trim() });
      setSuccess(`"${newName.trim()}" added successfully`);
      setNewName('');
      setShowAddModal(false);
      loadItems(activeTab);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add item');
    } finally { setAdding(false); }
  };

  const toggleItem = async (id) => {
    setError(''); setSuccess('');
    try {
      await api.put(`/admin/dropdowns/${activeTab}/${id}/toggle`);
      loadItems(activeTab);
    } catch { setError('Failed to update item'); }
  };

  // Step 1: click Delete — check if tickets exist
  const handleDeleteClick = async (item) => {
    setError(''); setSuccess('');
    if (item.ticket_count > 0) {
      // Show transfer modal
      setTransferItem(item);
    } else {
      // No tickets — confirm simple delete
      if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`)) return;
      await doDelete(item.id, null, item.name, 0);
    }
  };

  // Step 2: execute delete (with or without transfer)
  const doDelete = async (id, transferToId, name, count) => {
    setError(''); setSuccess('');
    try {
      const { data } = await api.delete(`/admin/dropdowns/${activeTab}/${id}`, {
        data: transferToId ? { transfer_to_id: transferToId } : {}
      });
      setSuccess(data.message);
      setTransferItem(null);
      loadItems(activeTab);
    } catch (err) {
      setError(err.response?.data?.message || 'Delete failed');
      setTransferItem(null);
    }
  };

  const activeTable   = TABLES.find(t => t.key === activeTab);
  const filtered      = items.filter(i => i.name.toLowerCase().includes(search.toLowerCase()));
  const activeCount   = items.filter(i =>  i.is_active).length;
  const inactiveCount = items.filter(i => !i.is_active).length;

  return (
    <Layout title="Dropdown Manager">

      {/* Transfer modal */}
      {transferItem && (
        <TransferModal
          item={transferItem}
          items={items}
          onConfirm={transferToId => doDelete(transferItem.id, transferToId, transferItem.name, transferItem.ticket_count)}
          onCancel={() => setTransferItem(null)}
        />
      )}

      {/* Tab bar */}
      <div style={{display:'flex',gap:4,flexWrap:'wrap',marginBottom:20,background:'#fff',border:'1px solid #e8e8f0',borderRadius:10,padding:6}}>
        {TABLES.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            style={{
              display:'flex',alignItems:'center',gap:6,
              padding:'7px 14px',borderRadius:7,border:'none',cursor:'pointer',
              fontSize:13,fontWeight:500,transition:'all 0.15s',
              background: activeTab === t.key ? '#6382e6' : 'transparent',
              color:      activeTab === t.key ? '#fff'    : '#555',
            }}>
            <i className={`fa-solid ${t.icon}`}></i>
            {t.label}
          </button>
        ))}
      </div>

      {/* Stats + Add button */}
      <div style={{display:'flex',gap:12,marginBottom:16,flexWrap:'wrap',alignItems:'center',justifyContent:'space-between'}}>
        <div style={{display:'flex',gap:12,flexWrap:'wrap'}}>
          <div style={{background:'#fff',border:'1px solid #e8e8f0',borderRadius:8,padding:'10px 16px',fontSize:13}}>
            <span style={{color:'#888'}}>Total: </span><span style={{fontWeight:600}}>{items.length}</span>
          </div>
          <div style={{background:'#EAF3DE',border:'1px solid #97C459',borderRadius:8,padding:'10px 16px',fontSize:13}}>
            <span style={{color:'#3B6D11'}}>Active: </span><span style={{fontWeight:600,color:'#3B6D11'}}>{activeCount}</span>
          </div>
          <div style={{background:'#F1EFE8',border:'1px solid #ccc',borderRadius:8,padding:'10px 16px',fontSize:13}}>
            <span style={{color:'#666'}}>Disabled: </span><span style={{fontWeight:600,color:'#666'}}>{inactiveCount}</span>
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => { setShowAddModal(true); setError(''); setSuccess(''); setNewName(''); }}>
          <i className="fa-solid fa-plus"></i> Add New {activeTable?.label.replace(/s$/, '')}
        </button>
      </div>

      {success && <div className="alert alert-success">{success}</div>}

      {/* Search bar */}
      <div className="card" style={{marginBottom:16, padding:'12px 16px'}}>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <i className="fa-solid fa-magnifying-glass" style={{color:'#aaa',fontSize:13}}></i>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder={`Search ${activeTable?.label.toLowerCase()}...`}
            style={{border:'none',outline:'none',fontSize:13,flex:1,background:'transparent'}} />
          {search && (
            <span style={{fontSize:12,color:'#aaa',cursor:'pointer'}} onClick={() => setSearch('')}>
              <i className="fa-solid fa-xmark"></i>
            </span>
          )}
        </div>
      </div>

      {/* Add New modal */}
      {showAddModal && (
        <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && setShowAddModal(false)}>
          <div className="modal" style={{maxWidth:420}}>
            <div className="modal-title">
              <i className={`fa-solid ${activeTable?.icon}`}></i>
              {' '}Add New {activeTable?.label.replace(/s$/, '')}
            </div>
            {error && <div className="alert alert-error">{error}</div>}
            <form onSubmit={addItem}>
              <div className="field" style={{marginBottom:20}}>
                <label>Name <span className="req">*</span></label>
                <input
                  type="text"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  placeholder={`Enter ${activeTable?.label.replace(/s$/, '').toLowerCase()} name...`}
                  autoFocus
                  required
                />
              </div>
              <div style={{display:'flex',justifyContent:'flex-end',gap:10}}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={adding || !newName.trim()}>
                  {adding ? <><i className="fa-solid fa-spinner fa-spin"></i> Adding...</> : <><i className="fa-solid fa-plus"></i> Add</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* List */}
      <div className="card" style={{padding:0}}>

        {loading ? (
          <div style={{padding:40,textAlign:'center',color:'#aaa'}}>
            <i className="fa-solid fa-spinner fa-spin" style={{fontSize:24}}></i>
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <i className="fa-solid fa-inbox"></i>
            <div>{search ? 'No matching items' : 'No items yet'}</div>
          </div>
        ) : (
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead>
              <tr>
                {['#','Name','Tickets','Status','Actions'].map((h,i) => (
                  <th key={i} style={{
                    textAlign: i===4 ? 'right' : 'left',
                    padding:'10px 16px',background:'#f8f9ff',
                    color:'#555',fontWeight:500,borderBottom:'1px solid #e8e8f0'
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((item, idx) => (
                <tr key={item.id} style={{opacity: item.is_active ? 1 : 0.55}}>
                  <td style={{padding:'10px 16px',borderBottom:'1px solid #f0f0f8',color:'#aaa',fontSize:12}}>
                    {idx+1}
                  </td>
                  <td style={{padding:'10px 16px',borderBottom:'1px solid #f0f0f8',fontWeight: item.is_active ? 500 : 400}}>
                    {item.name}
                    {!item.is_active && <span style={{marginLeft:8,fontSize:11,color:'#aaa'}}>(disabled)</span>}
                  </td>
                  <td style={{padding:'10px 16px',borderBottom:'1px solid #f0f0f8'}}>
                    {item.ticket_count > 0 ? (
                      <span style={{
                        background:'#E6F1FB',color:'#0C447C',
                        fontSize:11,padding:'2px 8px',borderRadius:99,fontWeight:500
                      }}>
                        {item.ticket_count} ticket{item.ticket_count !== 1 ? 's' : ''}
                      </span>
                    ) : (
                      <span style={{color:'#ccc',fontSize:12}}>—</span>
                    )}
                  </td>
                  <td style={{padding:'10px 16px',borderBottom:'1px solid #f0f0f8'}}>
                    <span className={`badge ${item.is_active ? 'badge-resolved' : 'badge-closed'}`}>
                      {item.is_active ? 'Active' : 'Disabled'}
                    </span>
                  </td>
                  <td style={{padding:'10px 16px',borderBottom:'1px solid #f0f0f8',textAlign:'right'}}>
                    <div style={{display:'flex',gap:6,justifyContent:'flex-end'}}>
                      <button className="btn btn-sm btn-secondary"
                        onClick={() => toggleItem(item.id)}
                        title={item.is_active ? 'Disable' : 'Enable'}>
                        <i className={`fa-solid ${item.is_active ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                        {item.is_active ? ' Disable' : ' Enable'}
                      </button>
                      <button className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteClick(item)}
                        title="Delete">
                        <i className="fa-solid fa-trash"></i>
                        {item.ticket_count > 0 && (
                          <span style={{marginLeft:4,fontSize:10,background:'rgba(255,255,255,0.3)',padding:'1px 5px',borderRadius:99}}>
                            {item.ticket_count}
                          </span>
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={{marginTop:12,fontSize:12,color:'#aaa'}}>
        <i className="fa-solid fa-circle-info" style={{marginRight:5}}></i>
        <strong>Tip:</strong> If an item has tickets, deleting it will prompt you to transfer those tickets to another item first.
        Use <strong>Disable</strong> to hide an item from dropdowns without affecting existing tickets.
      </div>

    </Layout>
  );
}